import { supabase } from '../config/supabaseClient';
import { withErrorHandling } from '../utils/errorHandler';
import { createAppError, ErrorCategory } from '../utils/errorHandler';
import { 
  SocialPost, 
  PlatformPostDetail, 
  PostAnalytics,
  MediaItem,
  PostTemplate,
  SocialPlatform,
  ValidationResult,
  ValidationError,
  PlatformRequirements,
  PLATFORM_REQUIREMENTS,
  MediaFileType,
  Campaign,
  HashtagCollection
} from '../types/socialMediaPlanner';

/**
 * Service for interacting with the Social Media Planner features
 */
export class SocialMediaPlannerService {
  /**
   * Validate user authentication
   */
  async validateAuthentication(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw createAppError(
        'Not authenticated',
        ErrorCategory.AUTH,
        401,
        'You must be logged in to use this feature'
      );
    }
    return session.user.id;
  }

  /**
   * Get all posts for the current user
   */
  async getPosts(): Promise<SocialPost[]> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        console.log('Fetching social posts for user:', userId);
        
        const { data, error } = await supabase
          .from('social_posts')
          .select(`
            *,
            platform_details:platform_post_details(*)
          `)
          .eq('user_id', userId)
          .order('scheduled_for', { ascending: true });

        if (error) {
          console.error('Error fetching posts:', error);
          throw createAppError(
            'Failed to fetch posts', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error loading your social posts',
            { error }
          );
        }

        return data || [];
      } catch (err: any) {
        console.error('Error in getPosts:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to fetch posts', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error loading your posts',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'getPosts' });
  }

  /**
   * Get posts for a specific date range
   */
  async getPostsForDateRange(startDate: Date, endDate: Date): Promise<SocialPost[]> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        console.log('Fetching posts for date range:', { startDate, endDate, userId });
        
        const { data, error } = await supabase
          .from('social_posts')
          .select(`
            *,
            platform_details:platform_post_details(*)
          `)
          .eq('user_id', userId)
          .gte('scheduled_for', startDate.toISOString())
          .lte('scheduled_for', endDate.toISOString())
          .order('scheduled_for', { ascending: true });

        if (error) {
          console.error('Error fetching posts for date range:', error);
          throw createAppError(
            'Failed to fetch posts for date range', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error loading your social posts',
            { error }
          );
        }

        return data || [];
      } catch (err: any) {
        console.error('Error in getPostsForDateRange:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to fetch posts for date range', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error loading your posts',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'getPostsForDateRange' });
  }

  /**
   * Create a new post
   */
  async createPost(post: SocialPost): Promise<SocialPost> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        // Create the main post
        const { data, error } = await supabase
          .from('social_posts')
          .insert({
            user_id: userId,
            content: post.content,
            media_urls: post.media_urls || [],
            platforms: post.platforms,
            scheduled_for: post.scheduled_for,
            status: post.status || 'draft',
            hashtags: post.hashtags || []
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating post:', error);
          throw createAppError(
            'Failed to create post', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error saving your post',
            { error }
          );
        }

        // If there are platform-specific details, save them
        if (post.platform_details && post.platform_details.length > 0) {
          const platformDetails = post.platform_details.map(detail => ({
            post_id: data.id,
            platform: detail.platform,
            platform_specific_content: detail.platform_specific_content,
            image_alt: detail.image_alt,
            link_preview_enabled: detail.link_preview_enabled || true
          }));

          const { error: detailsError } = await supabase
            .from('platform_post_details')
            .insert(platformDetails);

          if (detailsError) {
            console.error('Error saving platform details:', detailsError);
            // Don't throw here, consider it a partial success
          }
        }

        return data;
      } catch (err: any) {
        console.error('Error in createPost:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to create post', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error saving your post',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'createPost' });
  }

  /**
   * Update an existing post
   */
  async updatePost(post: SocialPost): Promise<SocialPost> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        if (!post.id) {
          throw createAppError(
            'Invalid post ID', 
            ErrorCategory.VALIDATION, 
            undefined, 
            'Cannot update post: missing post ID'
          );
        }
        
        // First verify the post belongs to this user
        const { data: existingPost, error: fetchError } = await supabase
          .from('social_posts')
          .select('*')
          .eq('id', post.id)
          .eq('user_id', userId)
          .single();
          
        if (fetchError || !existingPost) {
          console.error('Error fetching post to update:', fetchError);
          throw createAppError(
            'Failed to update post', 
            ErrorCategory.STORAGE, 
            undefined, 
            'Could not find the post to update',
            { error: fetchError }
          );
        }

        // Update the main post
        const { data, error } = await supabase
          .from('social_posts')
          .update({
            content: post.content,
            media_urls: post.media_urls || [],
            platforms: post.platforms,
            scheduled_for: post.scheduled_for,
            status: post.status,
            hashtags: post.hashtags || [],
            updated_at: new Date().toISOString()
          })
          .eq('id', post.id)
          .eq('user_id', userId)
          .select()
          .single();

        if (error) {
          console.error('Error updating post:', error);
          throw createAppError(
            'Failed to update post', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error updating your post',
            { error }
          );
        }

        // If there are platform-specific details, update them
        if (post.platform_details && post.platform_details.length > 0) {
          // Remove existing platform details
          await supabase
            .from('platform_post_details')
            .delete()
            .eq('post_id', post.id);
            
          // Add new platform details
          const platformDetails = post.platform_details.map(detail => ({
            post_id: post.id,
            platform: detail.platform,
            platform_specific_content: detail.platform_specific_content,
            image_alt: detail.image_alt,
            link_preview_enabled: detail.link_preview_enabled || true
          }));

          const { error: detailsError } = await supabase
            .from('platform_post_details')
            .insert(platformDetails);

          if (detailsError) {
            console.error('Error updating platform details:', detailsError);
          }
        }

        return data;
      } catch (err: any) {
        console.error('Error in updatePost:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to update post', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error updating your post',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'updatePost' });
  }

  /**
   * Delete a post
   */
  async deletePost(postId: string): Promise<void> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        // Delete the post (cascade will handle platform details)
        const { error } = await supabase
          .from('social_posts')
          .delete()
          .eq('id', postId)
          .eq('user_id', userId);

        if (error) {
          console.error('Error deleting post:', error);
          throw createAppError(
            'Failed to delete post', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error deleting your post',
            { error }
          );
        }
      } catch (err: any) {
        console.error('Error in deletePost:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to delete post', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error deleting your post',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'deletePost' });
  }

  /**
   * Upload media to the media library
   */
  async uploadMedia(file: File, altText?: string): Promise<MediaItem> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        // Generate a unique filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${userId}/${Date.now()}.${fileExt}`;
        
        // Upload the file to storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('media_library')
          .upload(fileName, file);
          
        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          throw createAppError(
            'Failed to upload media', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error uploading your media file',
            { error: uploadError }
          );
        }
          
        // Get the public URL
        const { data: { publicUrl } } = supabase.storage
          .from('media_library')
          .getPublicUrl(fileName);
          
        // Create a record in the media library table
        const mediaItem: Partial<MediaItem> = {
          user_id: userId,
          file_name: file.name,
          file_type: file.type.startsWith('image/') ? 'image' : 
                    file.type.startsWith('video/') ? 'video' : 'gif',
          file_url: publicUrl,
          alt_text: altText
        };
        
        const { data: dbData, error: dbError } = await supabase
          .from('media_library')
          .insert(mediaItem)
          .select()
          .single();
          
        if (dbError) {
          console.error('Error saving media item to database:', dbError);
          throw createAppError(
            'Failed to save media record', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error saving your media file information',
            { error: dbError }
          );
        }
        
        return dbData;
      } catch (err: any) {
        console.error('Error in uploadMedia:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to upload media', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error uploading your media',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'uploadMedia' });
  }

  /**
   * Get media items from the library
   */
  async getMediaLibrary(limit = 20, offset = 0): Promise<MediaItem[]> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        const { data, error } = await supabase
          .from('media_library')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .range(offset, offset + limit - 1);
          
        if (error) {
          console.error('Error fetching media library:', error);
          throw createAppError(
            'Failed to fetch media library', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error loading your media files',
            { error }
          );
        }
        
        return data || [];
      } catch (err: any) {
        console.error('Error in getMediaLibrary:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to fetch media library', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error loading your media library',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'getMediaLibrary' });
  }

  /**
   * Validates a social post against platform requirements
   * Returns validation results with any errors and warnings
   */
  validatePost(post: SocialPost): ValidationResult {
    // Handle synchronous validation outside of withErrorHandling
    // since withErrorHandling expects an async function
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];
    
    // Skip validation for drafts without platforms selected
    if (!post.platforms || post.platforms.length === 0) {
      return { isValid: true, errors: [], warnings: [] };
    }
      
    // Validate each platform's requirements
    post.platforms.forEach(platform => {
      // Check content length limits
      const platformRequirements = PLATFORM_REQUIREMENTS[platform];
      const platformDetails = post.platform_details?.find(detail => detail.platform === platform);
      const contentToCheck = platformDetails?.platform_specific_content || post.content;
      
      // Check character limits
      this.validateContentLength(contentToCheck, platform, platformRequirements, errors, warnings);
      
      // Check media requirements
      if (post.media_urls && post.media_urls.length > 0) {
        this.validateMediaRequirements(post.media_urls, platform, platformRequirements, errors, warnings);
      }
      
      // Validate hashtags
      if (post.hashtags && post.hashtags.length > 0) {
        this.validateHashtags(post.hashtags, platform, platformRequirements, errors, warnings);
      }
    });
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }
  
  /**
   * Validate content length against platform requirements
   */
  private validateContentLength(
    content: string, 
    platform: SocialPlatform, 
    requirements: PlatformRequirements,
    errors: ValidationError[],
    warnings: ValidationError[]
  ): void {
    const contentLength = content.length;
    
    if (contentLength > requirements.maxCharCount) {
      errors.push({
        code: 'CONTENT_TOO_LONG',
        field: 'content',
        message: `Content exceeds maximum ${requirements.maxCharCount} characters for ${platform}`,
        platform,
        severity: 'error'
      });
    } else if (contentLength > requirements.maxCharCount * 0.9) { // Warning at 90% of limit
      warnings.push({
        code: 'CONTENT_NEAR_LIMIT',
        field: 'content',
        message: `Content is nearing the ${requirements.maxCharCount} character limit for ${platform}`,
        platform,
        severity: 'warning'
      });
    } else if (contentLength < 5) {
      warnings.push({
        code: 'CONTENT_TOO_SHORT',
        field: 'content',
        message: `Content may be too short for effective engagement on ${platform}`,
        platform,
        severity: 'warning'
      });
    }
  }
  
  /**
   * Validate media files against platform requirements
   */
  private validateMediaRequirements(
    mediaUrls: string[], 
    platform: SocialPlatform, 
    requirements: PlatformRequirements,
    errors: ValidationError[],
    warnings: ValidationError[]
  ): void {
    // Check number of media files
    const imageCount = mediaUrls.filter(url => this.isImageFile(url)).length;
    const videoCount = mediaUrls.filter(url => this.isVideoFile(url)).length;
    
    if (imageCount > requirements.maxImages) {
      errors.push({
        code: 'TOO_MANY_IMAGES',
        field: 'media_urls',
        message: `${platform} supports maximum ${requirements.maxImages} images per post`,
        platform,
        severity: 'error'
      });
    }
    
    if (videoCount > requirements.maxVideos) {
      errors.push({
        code: 'TOO_MANY_VIDEOS',
        field: 'media_urls',
        message: `${platform} supports maximum ${requirements.maxVideos} videos per post`,
        platform,
        severity: 'error'
      });
    }
    
    // File type warnings - would ideally check actual file types, but we'll just check extensions
    // In a real implementation, we would validate MIME types server-side as well
    mediaUrls.forEach(url => {
      const extension = this.getFileExtension(url);
      if (!requirements.supportedFileTypes.includes(extension)) {
        warnings.push({
          code: 'UNSUPPORTED_FILE_TYPE',
          field: 'media_urls',
          message: `${extension} files may not be supported on ${platform}`,
          platform,
          severity: 'warning'
        });
      }
    });
  }
  
  /**
   * Validate hashtags against platform requirements
   */
  private validateHashtags(
    hashtags: string[], 
    platform: SocialPlatform, 
    requirements: PlatformRequirements,
    errors: ValidationError[],
    warnings: ValidationError[]
  ): void {
    if (requirements.maxHashtags && hashtags.length > requirements.maxHashtags) {
      warnings.push({
        code: 'TOO_MANY_HASHTAGS',
        field: 'hashtags',
        message: `${platform} works best with ${requirements.maxHashtags} or fewer hashtags`,
        platform,
        severity: 'warning'
      });
    }
    
    // Check for hashtags without # prefix and add warning
    const invalidHashtags = hashtags.filter(tag => !tag.startsWith('#'));
    if (invalidHashtags.length > 0) {
      warnings.push({
        code: 'INVALID_HASHTAG_FORMAT',
        field: 'hashtags',
        message: `Some hashtags are missing the # prefix`,
        platform,
        severity: 'warning'
      });
    }
    
    // Check for excessively long hashtags
    const longHashtags = hashtags.filter(tag => tag.length > 30);
    if (longHashtags.length > 0) {
      warnings.push({
        code: 'HASHTAG_TOO_LONG',
        field: 'hashtags',
        message: `Some hashtags are very long and may not be effective`,
        platform,
        severity: 'warning'
      });
    }
  }
  
  /**
   * Helper to check if a file is an image based on URL
   */
  private isImageFile(url: string): boolean {
    const extension = this.getFileExtension(url);
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
  }
  
  /**
   * Helper to check if a file is a video based on URL
   */
  private isVideoFile(url: string): boolean {
    const extension = this.getFileExtension(url);
    return ['mp4', 'mov', 'avi', 'wmv', 'webm'].includes(extension);
  }
  
  /**
   * Helper to get file extension from URL or path
   */
  private getFileExtension(url: string): string {
    // Remove query parameters if any
    const baseUrl = url.split('?')[0];
    // Get the extension
    const extension = baseUrl.split('.').pop()?.toLowerCase() || '';
    return extension;
  }

  /**
   * Get all campaigns for the current user
   */
  async getCampaigns(): Promise<Campaign[]> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        console.log('Fetching campaigns for user:', userId);
        
        const { data, error } = await supabase
          .from('campaigns')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching campaigns:', error);
          throw createAppError(
            'Failed to fetch campaigns', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error loading your campaigns',
            { error }
          );
        }

        return data || [];
      } catch (err: any) {
        console.error('Error in getCampaigns:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to fetch campaigns', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error loading your campaigns',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'getCampaigns' });
  }

  /**
   * Get campaign by ID
   */
  async getCampaign(campaignId: string): Promise<Campaign> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        const { data, error } = await supabase
          .from('campaigns')
          .select('*')
          .eq('id', campaignId)
          .eq('user_id', userId)
          .single();

        if (error) {
          console.error('Error fetching campaign:', error);
          throw createAppError(
            'Failed to fetch campaign', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error loading the campaign',
            { error }
          );
        }

        if (!data) {
          throw createAppError(
            'Campaign not found', 
            ErrorCategory.NOT_FOUND, 
            404, 
            'The requested campaign could not be found'
          );
        }

        return data as Campaign;
      } catch (err: any) {
        console.error('Error in getCampaign:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to fetch campaign', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error loading the campaign',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'getCampaign' });
  }

  /**
   * Create a new campaign
   */
  async createCampaign(campaign: Campaign): Promise<Campaign> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        // Ensure user_id is set
        const newCampaign = {
          ...campaign,
          user_id: userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        const { data, error } = await supabase
          .from('campaigns')
          .insert(newCampaign)
          .select()
          .single();

        if (error) {
          console.error('Error creating campaign:', error);
          throw createAppError(
            'Failed to create campaign', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error creating your campaign',
            { error }
          );
        }

        return data as Campaign;
      } catch (err: any) {
        console.error('Error in createCampaign:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to create campaign', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error creating your campaign',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'createCampaign' });
  }

  /**
   * Update an existing campaign
   */
  async updateCampaign(campaign: Campaign): Promise<Campaign> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        if (!campaign.id) {
          throw createAppError(
            'Campaign ID is required', 
            ErrorCategory.VALIDATION, 
            400, 
            'Campaign ID is required for updates'
          );
        }
        
        // Ensure user_id matches current user and update timestamp
        const updatedCampaign = {
          ...campaign,
          user_id: userId, // Ensure user_id is not changed
          updated_at: new Date().toISOString()
        };
        
        // Only update the campaign if it belongs to the current user
        const { data, error } = await supabase
          .from('campaigns')
          .update(updatedCampaign)
          .eq('id', campaign.id)
          .eq('user_id', userId)
          .select()
          .single();

        if (error) {
          console.error('Error updating campaign:', error);
          throw createAppError(
            'Failed to update campaign', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error updating your campaign',
            { error }
          );
        }

        if (!data) {
          throw createAppError(
            'Campaign not found', 
            ErrorCategory.NOT_FOUND, 
            404, 
            'The campaign you are trying to update could not be found'
          );
        }

        return data as Campaign;
      } catch (err: any) {
        console.error('Error in updateCampaign:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to update campaign', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error updating your campaign',
          { originalError: err }
        );
      }
    });
  }
  async deleteCampaign(campaignId: string): Promise<void> {
    return withErrorHandling(async () => {
      // Get user ID and validate authentication
      const userId = await this.validateAuthentication();
      
      // Check if the campaign exists and belongs to the user
      const { data: campaign } = await supabase
        .from('campaigns')
        .select('id')
        .eq('id', campaignId)
        .eq('user_id', userId)
        .single();
        
      if (!campaign) {
        throw createAppError(
          'Campaign not found', 
          ErrorCategory.NOT_FOUND, 
          404, 
          'The campaign you are trying to delete could not be found'
        );
      }
      
      // Delete the campaign
      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', campaignId);
      
      if (error) {
        console.error('Error deleting campaign:', error);
        throw createAppError(
          'Failed to delete campaign', 
          ErrorCategory.STORAGE, 
          undefined, 
          'There was an error deleting the campaign',
          { error }
        );
      }
    });
  }
  
  /**
   * Get all hashtag collections for the current user
   */
  async getHashtagCollections(): Promise<HashtagCollection[]> {
    return withErrorHandling(async () => {
      // Get user ID and validate authentication
      const userId = await this.validateAuthentication();
      
      console.log('Fetching hashtag collections for user:', userId);
      
      const { data, error } = await supabase
        .from('hashtag_collections')
        .select('*')
        .eq('user_id', userId)
        .order('name');
        
      if (error) {
        console.error('Error fetching hashtag collections:', error);
        throw createAppError(
          'Failed to fetch hashtag collections', 
          ErrorCategory.STORAGE, 
          undefined, 
          'There was an error loading your hashtag collections',
          { error }
        );
      }
      
      return data || [];
    });
  }
  
  /**
   * Get a specific hashtag collection by ID
   */
  async getHashtagCollection(collectionId: string): Promise<HashtagCollection> {
    return withErrorHandling(async () => {
      // Get user ID and validate authentication
      const userId = await this.validateAuthentication();
      
      // Get hashtag collection
      const { data, error } = await supabase
        .from('hashtag_collections')
        .select('*')
        .eq('id', collectionId)
        .eq('user_id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching hashtag collection:', error);
        throw createAppError(
          'Failed to fetch hashtag collection', 
          ErrorCategory.STORAGE, 
          undefined, 
          'There was an error loading the hashtag collection',
          { error }
        );
      }
      
      if (!data) {
        throw createAppError(
          'Hashtag collection not found', 
          ErrorCategory.NOT_FOUND, 
          404, 
          'The requested hashtag collection could not be found'
        );
      }
      
      return data;
    });
  }
  
  /**
   * Create a new hashtag collection
   */
  async createHashtagCollection(collection: HashtagCollection): Promise<HashtagCollection> {
    return withErrorHandling(async () => {
      // Get user ID and validate authentication
      const userId = await this.validateAuthentication();
      
      // Set the user ID and timestamps
      const newCollection = {
        ...collection,
        user_id: userId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        usage_count: 0
      };
      
      // Insert the hashtag collection
      const { data, error } = await supabase
        .from('hashtag_collections')
        .insert([newCollection])
        .select()
        .single();
      
      if (error) {
        console.error('Error creating hashtag collection:', error);
        throw createAppError(
          'Failed to create hashtag collection', 
          ErrorCategory.STORAGE, 
          undefined, 
          'There was an error creating the hashtag collection',
          { error }
        );
      }
      
      return data;
    });
  }
  
  /**
   * Update an existing hashtag collection
   */
  async updateHashtagCollection(collection: HashtagCollection): Promise<HashtagCollection> {
    return withErrorHandling(async () => {
      // Get user ID and validate authentication
      const userId = await this.validateAuthentication();
      
      // Ensure the collection has an ID
      if (!collection.id) {
        throw createAppError(
          'Missing collection ID', 
          ErrorCategory.VALIDATION, 
          400, 
          'The hashtag collection ID is required for updates'
        );
      }
      
      // Check if the collection exists and belongs to the user
      const { data: existingCollection } = await supabase
        .from('hashtag_collections')
        .select('id')
        .eq('id', collection.id)
        .eq('user_id', userId)
        .single();
        
      if (!existingCollection) {
        throw createAppError(
          'Hashtag collection not found', 
          ErrorCategory.NOT_FOUND, 
          404, 
          'The hashtag collection you are trying to update could not be found'
        );
      }
      
      // Update the collection
      const updates = {
        ...collection,
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('hashtag_collections')
        .update(updates)
        .eq('id', collection.id)
        .select()
        .single();
      
      if (error) {
        console.error('Error updating hashtag collection:', error);
        throw createAppError(
          'Failed to update hashtag collection', 
          ErrorCategory.STORAGE, 
          undefined, 
          'There was an error updating the hashtag collection',
          { error }
        );
      }
      
      return data;
    });
  }
  
  /**
   * Delete a hashtag collection by ID
   */
  async deleteHashtagCollection(collectionId: string): Promise<void> {
    return withErrorHandling(async () => {
      // Get user ID and validate authentication
      const userId = await this.validateAuthentication();
      
      // Check if the collection exists and belongs to the user
      const { data: collection } = await supabase
        .from('hashtag_collections')
        .select('id')
        .eq('id', collectionId)
        .eq('user_id', userId)
        .single();
        
      if (!collection) {
        throw createAppError(
          'Hashtag collection not found', 
          ErrorCategory.NOT_FOUND, 
          404, 
          'The hashtag collection you are trying to delete could not be found'
        );
      }
      
      // Delete the collection
      const { error } = await supabase
        .from('hashtag_collections')
        .delete()
        .eq('id', collectionId);
      
      if (error) {
        console.error('Error deleting hashtag collection:', error);
        throw createAppError(
          'Failed to delete hashtag collection', 
          ErrorCategory.STORAGE, 
          undefined, 
          'There was an error deleting the hashtag collection',
          { error }
        );
      }
    });
  }
  
  /**
   * Increment usage count for a hashtag collection
   */
  async incrementHashtagCollectionUsage(collectionId: string): Promise<void> {
    return withErrorHandling(async () => {
      // Get user ID and validate authentication
      const userId = await this.validateAuthentication();
      
      // Check if the collection exists and belongs to the user
      const { data: collection } = await supabase
        .from('hashtag_collections')
        .select('id, usage_count')
        .eq('id', collectionId)
        .eq('user_id', userId)
        .single();
        
      if (!collection) {
        throw createAppError(
          'Hashtag collection not found', 
          ErrorCategory.NOT_FOUND, 
          404, 
          'The hashtag collection could not be found'
        );
      }
      
      // Increment the usage count
      const { error } = await supabase
        .from('hashtag_collections')
        .update({ 
          usage_count: (collection.usage_count || 0) + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', collectionId);
      
      if (error) {
        console.error('Error updating hashtag collection usage:', error);
        throw createAppError(
          'Failed to update hashtag collection usage', 
          ErrorCategory.STORAGE, 
          undefined, 
          'There was an error updating the hashtag collection usage count',
          { error }
        );
      }
    });
  }
  
  /**
   * Add a post to a campaign
   */
  async addPostToCampaign(campaignId: string, postId: string): Promise<void> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        // First, get the campaign to verify ownership and current posts
        const { data: campaignData, error: campaignError } = await supabase
          .from('campaigns')
          .select('*')
          .eq('id', campaignId)
          .eq('user_id', userId)
          .single();

        if (campaignError || !campaignData) {
          console.error('Error fetching campaign:', campaignError);
          throw createAppError(
            'Campaign not found', 
            ErrorCategory.NOT_FOUND, 
            404, 
            'The campaign could not be found',
            { error: campaignError }
          );
        }

        const campaign = campaignData as Campaign;
        
        // Verify the post exists and belongs to the user
        const { data: postData, error: postError } = await supabase
          .from('social_posts')
          .select('id')
          .eq('id', postId)
          .eq('user_id', userId)
          .single();

        if (postError || !postData) {
          console.error('Error fetching post:', postError);
          throw createAppError(
            'Post not found', 
            ErrorCategory.NOT_FOUND, 
            404, 
            'The post could not be found',
            { error: postError }
          );
        }

        // Add the post to the campaign's posts array if not already present
        const posts = campaign.posts || [];
        if (!posts.includes(postId)) {
          posts.push(postId);
        }

        // Update the campaign with the new posts array
        const { error: updateError } = await supabase
          .from('campaigns')
          .update({ 
            posts, 
            updated_at: new Date().toISOString() 
          })
          .eq('id', campaignId)
          .eq('user_id', userId);

        if (updateError) {
          console.error('Error updating campaign with post:', updateError);
          throw createAppError(
            'Failed to add post to campaign', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error adding the post to your campaign',
            { error: updateError }
          );
        }
      } catch (err: any) {
        console.error('Error in addPostToCampaign:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to add post to campaign', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error adding the post to your campaign',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'addPostToCampaign' });
  }

  /**
   * Remove a post from a campaign
   */
  async removePostFromCampaign(campaignId: string, postId: string): Promise<void> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        // Get the campaign to verify ownership and current posts
        const { data: campaignData, error: campaignError } = await supabase
          .from('campaigns')
          .select('*')
          .eq('id', campaignId)
          .eq('user_id', userId)
          .single();

        if (campaignError || !campaignData) {
          console.error('Error fetching campaign:', campaignError);
          throw createAppError(
            'Campaign not found', 
            ErrorCategory.NOT_FOUND, 
            404, 
            'The campaign could not be found',
            { error: campaignError }
          );
        }

        const campaign = campaignData as Campaign;
        
        // Remove the post from the campaign's posts array
        const posts = (campaign.posts || []).filter(id => id !== postId);

        // Update the campaign with the new posts array
        const { error: updateError } = await supabase
          .from('campaigns')
          .update({ 
            posts, 
            updated_at: new Date().toISOString() 
          })
          .eq('id', campaignId)
          .eq('user_id', userId);

        if (updateError) {
          console.error('Error removing post from campaign:', updateError);
          throw createAppError(
            'Failed to remove post from campaign', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error removing the post from your campaign',
            { error: updateError }
          );
        }
      } catch (err: any) {
        console.error('Error in removePostFromCampaign:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to remove post from campaign', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error removing the post from your campaign',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'removePostFromCampaign' });
  }

  /**
   * Get posts for a specific campaign
   */
  async getCampaignPosts(campaignId: string): Promise<SocialPost[]> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        // Get the campaign to verify ownership and get post IDs
        const { data: campaignData, error: campaignError } = await supabase
          .from('campaigns')
          .select('*')
          .eq('id', campaignId)
          .eq('user_id', userId)
          .single();

        if (campaignError || !campaignData) {
          console.error('Error fetching campaign:', campaignError);
          throw createAppError(
            'Campaign not found', 
            ErrorCategory.NOT_FOUND, 
            404, 
            'The campaign could not be found',
            { error: campaignError }
          );
        }

        const campaign = campaignData as Campaign;
        const postIds = campaign.posts || [];
        
        if (postIds.length === 0) {
          return [];
        }

        // Get all posts by their IDs
        const { data: postsData, error: postsError } = await supabase
          .from('social_posts')
          .select(`
            *,
            platform_details:platform_post_details(*)
          `)
          .in('id', postIds)
          .eq('user_id', userId);

        if (postsError) {
          console.error('Error fetching campaign posts:', postsError);
          throw createAppError(
            'Failed to fetch campaign posts', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error loading posts for this campaign',
            { error: postsError }
          );
        }

        return postsData || [];
      } catch (err: any) {
        console.error('Error in getCampaignPosts:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to fetch campaign posts', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error loading posts for this campaign',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'getCampaignPosts' });
  }

  /**
   * Generate post content using AI
   * Integrates with the existing AI capabilities of SocialPostGenerator
   */
  async generatePostContent(
    platform: SocialPlatform, 
    topic: string, 
    tone?: string,
    purpose?: string,
    audience?: string
  ): Promise<string> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        await this.validateAuthentication();
        
        // This would integrate with your existing AI generation service
        // For now, we'll return a placeholder
        return `This is a generated post about ${topic} for ${platform}. [PLACEHOLDER]`;
        
        // In a real implementation, you would call your AI service here
        // const response = await fetch('/api/generatePost', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({ platform, topic, tone, purpose, audience })
        // });
        // const data = await response.json();
        // return data.content;
      } catch (err: any) {
        console.error('Error in generatePostContent:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to generate post content', 
          ErrorCategory.AI, 
          undefined,
          'There was an error generating content for your post',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'generatePostContent' });
  }

  /**
   * Identify evergreen posts eligible for recycling based on their recycling settings
   * @returns Array of posts that should be recycled
   */
  async findEligibleEvergreenPosts(): Promise<SocialPost[]> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        // Get all evergreen posts that have been published
        const { data, error } = await supabase
          .from('social_posts')
          .select(`
            *,
            platform_details:platform_post_details(*)
          `)
          .eq('user_id', userId)
          .eq('is_evergreen', true)
          .in('status', ['published'])
          .order('published_at', { ascending: true });

        if (error) {
          console.error('Error fetching evergreen posts:', error);
          throw createAppError(
            'Failed to fetch evergreen posts', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error loading your evergreen posts',
            { error }
          );
        }

        if (!data || data.length === 0) {
          return [];
        }

        const now = new Date();
        const eligiblePosts: SocialPost[] = [];
        
        // Filter posts based on their recycling settings
        for (const post of data) {
          // Skip posts without recycling settings
          if (!post.recycling_settings) continue;
          
          // Check if post has been recycled before
          const lastRecycledDate = post.last_recycled_at 
            ? new Date(post.last_recycled_at)
            : post.published_at 
              ? new Date(post.published_at) 
              : null;
          
          if (!lastRecycledDate) continue;
          
          // Calculate if enough time has passed based on interval setting
          const intervalDays = post.recycling_settings.interval || 30;
          const nextRecycleDate = new Date(lastRecycledDate);
          nextRecycleDate.setDate(nextRecycleDate.getDate() + intervalDays);
          
          // Check if it's time to recycle
          if (now >= nextRecycleDate) {
            // Check if we've hit the max_recycles limit (0 = unlimited)
            const maxRecycles = post.recycling_settings.max_recycles || 0;
            const currentRecycles = post.recycle_count || 0;
            
            if (maxRecycles === 0 || currentRecycles < maxRecycles) {
              eligiblePosts.push(post);
            }
          }
        }
        
        return eligiblePosts;
      } catch (err: any) {
        console.error('Error in findEligibleEvergreenPosts:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to find eligible evergreen posts', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error processing your evergreen posts',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'findEligibleEvergreenPosts' });
  }
  
  /**
   * Recycle an evergreen post by creating a new post based on it
   * @param originalPost The original evergreen post to recycle
   * @returns The newly created post
   */
  async recyclePost(originalPost: SocialPost): Promise<SocialPost> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        if (!originalPost.id || !originalPost.is_evergreen) {
          throw createAppError(
            'Invalid post for recycling', 
            ErrorCategory.VALIDATION, 
            400, 
            'The selected post cannot be recycled'
          );
        }
        
        // Create a new post based on the original
        let newContent = originalPost.content;
        
        // If randomize_content is enabled, make small variations to avoid exact duplication
        if (originalPost.recycling_settings?.randomize_content) {
          // Create variations based on common social media patterns
          const variations = [
            `${newContent} #recycled`,
            `Sharing again: ${newContent}`,
            `Worth repeating: ${newContent}`,
            `In case you missed it: ${newContent}`,
            newContent
          ];
          newContent = variations[Math.floor(Math.random() * variations.length)];
        }
        
        // Determine scheduling time
        let scheduledFor: string | undefined;
        
        if (originalPost.recycling_settings?.use_best_time) {
          // Simple best time algorithm - in a real app would use engagement metrics
          // Schedule between 9am-6pm in the next 3 days
          const daysAhead = Math.floor(Math.random() * 3) + 1;
          const hour = Math.floor(Math.random() * 9) + 9; // 9am to 6pm
          
          const scheduledDate = new Date();
          scheduledDate.setDate(scheduledDate.getDate() + daysAhead);
          scheduledDate.setHours(hour, 0, 0, 0);
          scheduledFor = scheduledDate.toISOString();
        } else {
          // Schedule for tomorrow at the same time
          const scheduledDate = new Date();
          scheduledDate.setDate(scheduledDate.getDate() + 1);
          scheduledFor = scheduledDate.toISOString();
        }
        
        // Create the recycled post
        const newPost = {
          user_id: userId,
          content: newContent,
          platforms: originalPost.platforms,
          status: 'scheduled',
          scheduled_for: scheduledFor,
          hashtags: originalPost.hashtags || [],
          media_urls: originalPost.media_urls || [],
          color: originalPost.color,
          is_recycled: true,
          recycled_from: originalPost.id,
          // Don't copy the recycling settings to avoid infinite recycling chain
          is_evergreen: false,
        } as Partial<SocialPost>;
        
        // Insert the new post
        const { data, error } = await supabase
          .from('social_posts')
          .insert(newPost)
          .select()
          .single();

        if (error) {
          console.error('Error creating recycled post:', error);
          throw createAppError(
            'Failed to recycle post', 
            ErrorCategory.STORAGE, 
            undefined, 
            'There was an error creating your recycled post',
            { error }
          );
        }
        
        // Update the original post with the recycling info
        const { error: updateError } = await supabase
          .from('social_posts')
          .update({
            last_recycled_at: new Date().toISOString(),
            recycle_count: (originalPost.recycle_count || 0) + 1
          })
          .eq('id', originalPost.id)
          .eq('user_id', userId);
          
        if (updateError) {
          console.error('Error updating original post recycling data:', updateError);
          // Don't throw here - we consider it a success if the new post was created
        }
        
        // Handle platform-specific details if any
        if (originalPost.platform_details && originalPost.platform_details.length > 0) {
          const platformDetails = originalPost.platform_details.map(detail => ({
            post_id: data.id,
            platform: detail.platform,
            platform_specific_content: detail.platform_specific_content,
            image_alt: detail.image_alt,
            link_preview_enabled: detail.link_preview_enabled || true
          }));

          const { error: detailsError } = await supabase
            .from('platform_post_details')
            .insert(platformDetails);

          if (detailsError) {
            console.error('Error creating platform details for recycled post:', detailsError);
          }
        }

        return data;
      } catch (err: any) {
        console.error('Error in recyclePost:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to recycle post', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error recycling your post',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'recyclePost' });
  }
  
  /**
   * Process all eligible evergreen posts and recycle them
   * This would typically be called by a scheduled job
   * @returns Array of newly created recycled posts
   */
  async processEvergreenRecycling(): Promise<SocialPost[]> {
    return withErrorHandling(async () => {
      try {
        // Find all eligible posts
        const eligiblePosts = await this.findEligibleEvergreenPosts();
        
        if (eligiblePosts.length === 0) {
          console.log('No eligible evergreen posts found for recycling');
          return [];
        }
        
        console.log(`Found ${eligiblePosts.length} eligible posts for recycling`);
        
        // Recycle each eligible post
        const recycledPosts: SocialPost[] = [];
        
        for (const post of eligiblePosts) {
          try {
            console.log(`Recycling post ${post.id}`);
            const recycledPost = await this.recyclePost(post);
            recycledPosts.push(recycledPost);
          } catch (error) {
            console.error(`Failed to recycle post ${post.id}:`, error);
            // Continue with other posts even if one fails
          }
        }
        
        console.log(`Successfully recycled ${recycledPosts.length} posts`);
        return recycledPosts;
      } catch (err: any) {
        console.error('Error in processEvergreenRecycling:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to process evergreen recycling', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error during evergreen content recycling',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'processEvergreenRecycling' });
  }
  
  /**
   * Manually recycle a specific evergreen post, regardless of its eligibility
   * This is used for the manual recycle button in the UI
   * @param postId ID of the post to recycle
   * @returns The newly created post
   */
  async manualRecyclePost(postId: string): Promise<SocialPost> {
    return withErrorHandling(async () => {
      try {
        // Get user ID and validate authentication
        const userId = await this.validateAuthentication();
        
        // Get the original post
        const { data, error } = await supabase
          .from('social_posts')
          .select(`
            *,
            platform_details:platform_post_details(*)
          `)
          .eq('id', postId)
          .eq('user_id', userId)
          .eq('is_evergreen', true)
          .single();
          
        if (error || !data) {
          console.error('Error fetching post for manual recycling:', error);
          throw createAppError(
            'Post not found', 
            ErrorCategory.NOT_FOUND, 
            404, 
            'The post you\'re trying to recycle could not be found',
            { error }
          );
        }
        
        // Recycle the post
        return await this.recyclePost(data);
      } catch (err: any) {
        console.error('Error in manualRecyclePost:', err);
        
        if (err?.name === 'AppError') {
          throw err;
        }
        
        throw createAppError(
          'Failed to manually recycle post', 
          ErrorCategory.UNKNOWN, 
          undefined,
          'There was an unexpected error recycling your post',
          { originalError: err }
        );
      }
    }, { service: 'SocialMediaPlannerService', method: 'manualRecyclePost' });
  }
}
